import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { BACKEND_URL } from "../../constants/globals";
import { AddTransitionForm } from "./AddTransitionForm";
import {
    fetchDefinition,
    fetchMetadata,
    queryKeys,
    StaleWriteError,
} from "./api";
import { DefinitionForm } from "./DefinitionForm";
import { StatusGraph } from "./StatusGraph";
import { TransitionEditor } from "./TransitionEditor";
import { TransitionList } from "./TransitionList";
import type { Transition } from "./types";
import { useEditorMutations } from "./useEditorMutations";
import { useUnsavedChanges } from "./useUnsavedChanges";
import { ValidationOverview } from "./ValidationOverview";

function LoadFailed({ error }: { error: unknown }) {
    return (
        <div className="aer-wf-notice aer-wf-notice--danger" role="alert">
            <p>
                <strong>This definition could not be loaded.</strong>
            </p>
            <p>{error instanceof Error ? error.message : String(error)}</p>
        </div>
    );
}

export function WorkflowEditor({ definitionId }: { definitionId: string }) {
    const [selectedTransitionId, setSelectedTransitionId] = useState<
        string | null
    >(null);

    const mutations = useEditorMutations(definitionId);
    const { isDirty, dirtyCount, markDirty, markClean, confirmDiscard } =
        useUnsavedChanges();

    // One callback for every editor to report its draft state through, so the guard counts
    // things the user would lose rather than tracking a single boolean.
    const onDirtyChange = useCallback(
        (key: string, dirty: boolean) => {
            if (dirty) {
                markDirty(key);
            } else {
                markClean(key);
            }
        },
        [markDirty, markClean],
    );

    /** Switching away from a transition abandons its drafts, so it goes through the guard. */
    const selectTransition = useCallback(
        (transition: Transition | null) => {
            const nextId = transition?.id ?? null;
            if (nextId === selectedTransitionId) {
                return;
            }
            if (!confirmDiscard("Selecting another transition")) {
                return;
            }
            setSelectedTransitionId(nextId);
        },
        [confirmDiscard, selectedTransitionId],
    );

    // retry:false because a rejected read is rejected on the retry too. No refetch on focus
    // either: this screen holds local drafts, and a refetch behind one is how they get lost.
    const queryOptions = { retry: false, refetchOnWindowFocus: false } as const;

    const definitionQuery = useQuery({
        queryKey: queryKeys.definition(definitionId),
        queryFn: () => fetchDefinition(definitionId),
        ...queryOptions,
    });
    const metadataQuery = useQuery({
        queryKey: queryKeys.metadata(definitionId),
        queryFn: () => fetchMetadata(definitionId),
        ...queryOptions,
    });

    const definition = definitionQuery.data;
    const metadata = metadataQuery.data;

    const selectedTransition: Transition | null = useMemo(() => {
        if (!definition || !selectedTransitionId) {
            return null;
        }
        return (
            definition.transitions.find(
                (transition) => transition.id === selectedTransitionId,
            ) ?? null
        );
    }, [definition, selectedTransitionId]);

    // A stale write is not a load failure — it is reported above the editor, which stays usable.
    const staleWriteMessage = useMemo(() => {
        const mutationError = Object.values(mutations).find(
            (mutation) => mutation.error instanceof StaleWriteError,
        )?.error;
        return mutationError instanceof StaleWriteError
            ? mutationError.message
            : null;
    }, [mutations]);

    const error = definitionQuery.error ?? metadataQuery.error;
    if (error) {
        return <LoadFailed error={error} />;
    }

    if (definitionQuery.isLoading || metadataQuery.isLoading) {
        return (
            <p className="aer-wf-notice" aria-busy="true">
                Loading the workflow definition…
            </p>
        );
    }

    if (!definition || !metadata) {
        return (
            <LoadFailed
                error={new Error("The server returned no definition.")}
            />
        );
    }

    return (
        <div className="aer-wf">
            <header className="aer-wf-header">
                <div>
                    <h2 className="aer-wf-header__name">{definition.name}</h2>
                    <p className="aer-wf-header__meta">
                        {definition.ownerName ?? "Global definition"}
                        {definition.isActive ? "" : " · inactive"}
                    </p>
                </div>
                <div className="aer-wf-header__side">
                    {isDirty && (
                        <p className="aer-wf-header__dirty" role="status">
                            {dirtyCount} unsaved{" "}
                            {dirtyCount === 1 ? "change" : "changes"}
                        </p>
                    )}
                    {/* Both interfaces are supported and enforce the same rules, so this points
                        at the other one rather than apologising for a gap. Duplicating a
                        definition to another company is the one thing only the admin form does.
                        Absolute: /admin on this origin is the SPA's own admin area, not
                        Django's. */}
                    <p className="aer-wf-header__note">
                        Also editable in the{" "}
                        <a
                            href={`${BACKEND_URL}/admin/workflows/workflowdef/${definition.id}/change/`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Django admin
                        </a>
                        , which is still where you duplicate a definition.
                    </p>
                </div>
            </header>

            {/* A stale write means someone else changed this definition. Refetching is the fix,
                and saying so beats a field error that makes no sense against any input. */}
            {staleWriteMessage && (
                <div className="aer-wf-notice aer-wf-notice--warn" role="alert">
                    <p>{staleWriteMessage}</p>
                    <p>
                        <button
                            type="button"
                            className="aer-wf-button"
                            onClick={() => definitionQuery.refetch()}
                        >
                            Reload this definition
                        </button>
                    </p>
                </div>
            )}

            <DefinitionForm
                definition={definition}
                metadata={metadata}
                mutations={mutations}
                onDirtyChange={onDirtyChange}
            />

            <ValidationOverview
                definition={definition}
                statuses={metadata.statuses}
                dataProviders={metadata.dataProviders}
                onSelectTransition={selectTransition}
            />

            <AddTransitionForm metadata={metadata} mutations={mutations} />

            {definition.transitions.length === 0 ? (
                <div className="aer-wf-notice">
                    <p>
                        <strong>This definition has no transitions yet.</strong>{" "}
                        Nothing will happen to an event under it. Add the first
                        one above.
                    </p>
                </div>
            ) : (
                <div className="aer-wf-layout">
                    <div className="aer-wf-layout__main">
                        <StatusGraph
                            definition={definition}
                            statuses={metadata.statuses}
                            selectedTransitionId={selectedTransitionId}
                            onSelectTransition={selectTransition}
                            onConnectStatuses={(fromStatus, toStatus) =>
                                mutations.addTransition.mutate({
                                    fromStatus: fromStatus,
                                    toStatus: toStatus,
                                    // Dragging says nothing about type; AUTO is the default the
                                    // panel can change, and the safer of the two to review.
                                    transitionType: "AUTO",
                                })
                            }
                        />
                        <TransitionList
                            transitions={definition.transitions}
                            metadata={metadata}
                            selectedTransitionId={selectedTransitionId}
                            onSelectTransition={selectTransition}
                        />
                    </div>
                    {selectedTransition ? (
                        <TransitionEditor
                            key={selectedTransition.id}
                            transition={selectedTransition}
                            metadata={metadata}
                            mutations={mutations}
                            onDirtyChange={onDirtyChange}
                            onDeleted={() => setSelectedTransitionId(null)}
                        />
                    ) : (
                        <aside
                            className="aer-wf-panel"
                            aria-label="Transition detail"
                        >
                            <p className="aer-wf-panel__empty">
                                Select a transition — on the graph or in the
                                list — to edit its actions and rules.
                            </p>
                        </aside>
                    )}
                </div>
            )}
        </div>
    );
}
